import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MonitorUpdateInput } from '@servwell/shared';
import { useTRPC } from '../../../lib/trpc';
import { awaitingServer, refreshUntilShown } from '../../ui/QueryStates';
import { useTapOnce } from '../../ui/useTapOnce';
import { sendOnce } from '../../tickets/sendOnce';

type Change = Omit<MonitorUpdateInput, 'clientId' | 'mutationId'>;

/**
 * One control's `monitor.update` (spec §7): one tap, one write (`sendOnce`), held pending until
 * Settings has read the monitors again (`refreshUntilShown`), so the switch or the form never
 * shows the old answer with its control live. A refusal is kept, in the server's words, for
 * the control to show where it was pressed.
 */
export function useMonitorUpdate() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const [error, setError] = useState<string | null>(null);
  const update = useMutation(
    trpc.monitor.update.mutationOptions({
      onSuccess: () => {
        setError(null);
        return refreshUntilShown(qc, { queryKey: trpc.monitor.health.queryKey() });
      },
      onError: (e) => setError(e.message),
      onSettled: () => lock.release(),
    }),
  );
  return {
    /** True when the write went out; `landed` runs once the monitors have been read again. */
    send(change: Change, landed?: () => void): boolean {
      return sendOnce(lock, (v) => update.mutate(v, { onSuccess: landed }), change);
    },
    busy: awaitingServer(update),
    error,
    clearError: () => setError(null),
  };
}
